import { useScatterplotContext } from 'features/timeSeries'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import { useSentinelOverlayMap } from 'features/timeSeries/hooks/useSentinelOverlayMap'
import {
  latestOverlayReading,
  SENTINEL_OUT_OF_BAND_COLOR,
  SIGMA_WARN,
} from 'features/timeSeries/hooks/useSentinelScores'
import React from 'react'
import styled from 'styled-components'

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 10%;
  background: ${({ theme }) => theme.forecast.cardBackground};
  gap: 0.25rem;
  padding: 0.5rem;
  border-left: 4px solid ${({ theme }) => theme.forecast.cardGroupBackground};
`

const TagNamesItem = styled.div<{ color: string }>`
  color: ${({ color }) => color};
  overflow: hidden;
  text-overflow: ellipsis;
`

const SentinelReadout = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.8125em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SigmaSpan = styled.span<{ $outOfBand: boolean }>`
  color: ${({ theme, $outOfBand }) =>
    $outOfBand ? SENTINEL_OUT_OF_BAND_COLOR : theme.forecast.secondaryText};
  font-size: 0.8125em;
`

export const TagNamesSidebar: React.FC = () => {
  const { customSensorColors, selectedSensors } = useScatterplotContext()
  const overlaysByTarget = useSentinelOverlayMap()

  return (
    <SidebarContainer>
      {selectedSensors.map((sensor, sensorIndex) => {
        const overlay = overlaysByTarget.get(sensor.id)
        const reading = overlay ? latestOverlayReading(overlay) : null

        return (
          <React.Fragment key={sensor.id}>
            {overlay && reading && (
              <SentinelReadout
                title={`${overlay.sentinelName} predicted value for ${sensor.data.name}`}>
                ◈ {overlay.sentinelName}: {reading.expected.toFixed(2)}
              </SentinelReadout>
            )}
            <TagNamesItem
              color={
                customSensorColors[sensor.id] || getSensorColor(sensorIndex)
              }>
              {sensor.data.name}{' '}
              {reading && (
                <SigmaSpan $outOfBand={Math.abs(reading.sigma) >= SIGMA_WARN}>
                  {reading.sigma > 0 ? '▲' : '▼'}
                  {Math.abs(reading.sigma).toFixed(1)}σ
                </SigmaSpan>
              )}
            </TagNamesItem>
          </React.Fragment>
        )
      })}
    </SidebarContainer>
  )
}
